import React from 'react'
import stepperStyles from './SignupNumberedStepper.module.css'
import { SignupStep } from '../../Steps/StepContainer/Steps'

const CHECKMARK_ICON = '/foodslaboEf/img/ProgressBanner/step-b/checkmark.svg'

const TOTAL_DOTS = 3

const DOT_LABELS = ['基本情報', '経歴', '確認']

type DotStatus = 'pending' | 'current' | 'completed'

// sectionDotは0〜2（各セクション内）に加え、確認画面では3（=TOTAL_DOTS、全セクション完了）を取りうる
const getDotStatus = (dotIndex: number, sectionDot: number): DotStatus => {
    if (sectionDot > dotIndex) return 'completed'
    if (sectionDot === dotIndex) return 'current'
    return 'pending'
}

// 指定したsectionDot値を持つステップの先頭・末尾indexを、配列から動的に求める
// （セクションごとのステップ数をハードコードせず、SIGNUP_STEPS_B/Cの実配列を単一の情報源にするため）
const getSectionStepRange = (steps: SignupStep[], sectionDotValue: number) => {
    const indices = steps.reduce<number[]>((acc, step, i) => {
        if (step.sectionDot === sectionDotValue) acc.push(i)
        return acc
    }, [])
    return { start: indices[0], end: indices[indices.length - 1] }
}

// connectorIndexが指す罫線（dotIndex `connectorIndex` と `connectorIndex + 1` の間）の塗り進捗（0〜100）
const getConnectorFillPercent = (
    steps: SignupStep[],
    currentStep: number,
    sectionDot: number,
    connectorIndex: number
): number => {
    if (sectionDot > connectorIndex) return 100
    if (sectionDot < connectorIndex) return 0

    const { start, end } = getSectionStepRange(steps, connectorIndex)
    if (start === undefined || end === undefined) return 0
    if (end <= start) return 100

    return ((currentStep - start) / (end - start)) * 100
}

const getConnectorFillColor = (
    sectionDot: number,
    connectorIndex: number
): string => (sectionDot > connectorIndex ? '#ff8000' : '#7a7775')

interface SignupNumberedStepperProps {
    steps: SignupStep[]
    currentStep: number
}

// DEV-3482: B/Cパターン専用の番号付き丸ステッパー。DEV-3012のAdFormProgressBanner（AdForm）と同じ設計。
// どのドットを現在地にするかは steps[].sectionDot だけで決まるため、パターンごとの分岐は持たない
export const SignupNumberedStepper = ({
    steps,
    currentStep,
}: SignupNumberedStepperProps) => {
    const sectionDot = steps[currentStep]?.sectionDot ?? 0

    return (
        <div className={stepperStyles.header}>
            <div className={stepperStyles.stepRow}>
                {Array.from({ length: TOTAL_DOTS }, (_, dotIndex) => {
                    const status = getDotStatus(dotIndex, sectionDot)

                    const connectorFillStyle = (
                        connectorIndex: number,
                        half: 'leading' | 'trailing'
                    ) => {
                        const overallPercent = getConnectorFillPercent(
                            steps,
                            currentStep,
                            sectionDot,
                            connectorIndex
                        )
                        const localPercent =
                            half === 'trailing'
                                ? Math.min(overallPercent * 2, 100)
                                : Math.max(overallPercent * 2 - 100, 0)
                        const fillColor = getConnectorFillColor(
                            sectionDot,
                            connectorIndex
                        )
                        return {
                            width: `${localPercent}%`,
                            backgroundColor: fillColor,
                        }
                    }

                    return (
                        <div key={dotIndex} className={stepperStyles.stepUnit}>
                            <div className={stepperStyles.stepLine}>
                                {dotIndex === 0 ? (
                                    <div
                                        className={
                                            stepperStyles.connectorSpacer
                                        }
                                    />
                                ) : (
                                    <div className={stepperStyles.connector}>
                                        <div
                                            className={
                                                stepperStyles.connectorFill
                                            }
                                            style={connectorFillStyle(
                                                dotIndex - 1,
                                                'leading'
                                            )}
                                        />
                                    </div>
                                )}
                                <div
                                    className={`${stepperStyles.circle} ${
                                        status === 'completed'
                                            ? stepperStyles.circleCompleted
                                            : status === 'current'
                                              ? stepperStyles.circleCurrent
                                              : stepperStyles.circlePending
                                    }`}
                                >
                                    {status === 'completed' ? (
                                        <img
                                            src={CHECKMARK_ICON}
                                            alt="完了"
                                            className={
                                                stepperStyles.checkmarkIcon
                                            }
                                        />
                                    ) : (
                                        <span
                                            className={
                                                stepperStyles.circleNumber
                                            }
                                        >
                                            {dotIndex + 1}
                                        </span>
                                    )}
                                </div>
                                {dotIndex === TOTAL_DOTS - 1 ? (
                                    <div
                                        className={
                                            stepperStyles.connectorSpacer
                                        }
                                    />
                                ) : (
                                    <div className={stepperStyles.connector}>
                                        <div
                                            className={
                                                stepperStyles.connectorFill
                                            }
                                            style={connectorFillStyle(
                                                dotIndex,
                                                'trailing'
                                            )}
                                        />
                                    </div>
                                )}
                            </div>
                            <span
                                className={`${stepperStyles.label} ${
                                    status === 'current'
                                        ? stepperStyles.labelCurrent
                                        : ''
                                }`}
                            >
                                {DOT_LABELS[dotIndex]}
                            </span>
                        </div>
                    )
                })}
            </div>
        </div>
    )
}
